/**
 * ConfirmDialog — Enterprise Design System
 *
 * A standardised confirmation dialog for destructive or important actions.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onConfirm: () => void
 *  - title: string (default: "Confirm Action")
 *  - description: string | ReactNode
 *  - confirmLabel: string (default: "Confirm")
 *  - cancelLabel: string (default: "Cancel")
 *  - variant: 'danger' | 'warning' | 'info' (default: 'danger')
 *  - loading: boolean — shows spinner on confirm button
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const VARIANT_CONFIG = {
  danger: {
    icon: ErrorOutlineIcon,
    iconBg: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
    iconColor: '#dc2626',
    confirmColor: 'error',
  },
  warning: {
    icon: WarningAmberIcon,
    iconBg: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
    iconColor: '#d97706',
    confirmColor: 'warning',
  },
  info: {
    icon: InfoOutlinedIcon,
    iconBg: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
    iconColor: '#2563eb',
    confirmColor: 'primary',
  },
};

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}) {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.danger;
  const IconComponent = config.icon;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: config.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ fontSize: 24, color: config.iconColor }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.0625rem' }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {typeof description === 'string' ? (
          <Typography
            id="confirm-dialog-description"
            variant="body2"
            color="text.secondary"
            sx={{ pl: 8, lineHeight: 1.6 }}
          >
            {description}
          </Typography>
        ) : (
          <Box id="confirm-dialog-description" sx={{ pl: 8 }}>
            {description}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
          sx={{ color: 'text.secondary' }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={config.confirmColor}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {loading ? 'Processing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
